import {
  getDocumentById,
  updateDocumentListData,
} from "@prismicio/editor-fields"
import {
  useDebounce,
  useOnChange,
  useStableCallback,
} from "@prismicio/editor-support/React"
import { updateData } from "@prismicio/editor-support/Suspense"
import { useAlert, useToast } from "@prismicio/editor-ui"
import {
  Document as DocumentT,
  isRichTextContent,
  isTextContent,
  isUIDContent,
  traverseDocument,
  type WidgetContent,
} from "@prismicio/types-internal/lib/content"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"

import { env } from "../app/env"
import { baseUrl, saveTitle } from "../app/services/core"
import { type DocumentData } from "./documentData"
import { extractRelevantTextFromContent } from "./extractRelevantTextFromContent"
import { type DocumentDraft } from "./useDraft"

export interface DocumentTitle {
  displayedTitle: string
  title: string | undefined
  draftTitle: string | undefined
  onEditComplete: () => void
  setDraftTitle: (value: string | undefined) => void
}

export function useDocumentTitle(
  data: DocumentData,
  draft: DocumentDraft,
): DocumentTitle {
  const title = data.type === "existing" ? data.meta.title : data.asCopy?.title
  const [draftTitle, setDraftTitle] = useState(title)

  useOnChange(title, setDraftTitle)

  const { showToast } = useToast()
  const { showAlert } = useAlert()

  const queryClient = useQueryClient()

  const displayedTitle = draftTitle ?? title ?? "Untitled document"
  const shouldGetTitleFromDocument = useRef(data.type === "new" && !draftTitle)

  const setDraftTitleFromDocument = useStableCallback(() => {
    if (!shouldGetTitleFromDocument.current) return

    const title =
      findUseAsTitleContent(draft.document, data.customType) ??
      extractRelevantTextFromContent(draft.document)

    setDraftTitle(title)
  })

  useDebounce(
    shouldGetTitleFromDocument.current ? draft.document : undefined,
    200,
    setDraftTitleFromDocument,
  )

  function disableTitleFromDocumentIfNeeded() {
    if (shouldGetTitleFromDocument.current) {
      shouldGetTitleFromDocument.current = false
    }
  }
  function enableTitleFromDocumentIfNeeded() {
    if (draftTitle || data.type !== "new") return
    shouldGetTitleFromDocument.current = true
    setDraftTitleFromDocument()
  }

  async function onEditComplete() {
    if (data.type === "new") {
      enableTitleFromDocumentIfNeeded()
      return
    }

    if (!draftTitle || draftTitle === title) return

    const documentId = data.meta.id

    try {
      await saveTitle({ title: draftTitle, id: documentId })

      const updatedMeta = { ...data.meta, title: draftTitle }
      updateData(
        getDocumentById,
        [baseUrl, env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY, documentId],
        updatedMeta,
      )

      updateDocumentListData(queryClient, (document) =>
        document.id === updatedMeta.id ? updatedMeta : document,
      )

      showToast({
        title: "Your title has been saved.",
      })
    } catch {
      showAlert({
        title: "Oops! We were unable to save your title.",
        subtitle:
          "We encountered an error while saving your title. Please contact our support team if this issue persists.",
        onRetry: onEditComplete,
      })
    } finally {
      setDraftTitle(undefined)
    }
  }

  return {
    displayedTitle,
    title,
    draftTitle,
    onEditComplete,
    setDraftTitle: (value: string | undefined) => {
      disableTitleFromDocumentIfNeeded()
      setDraftTitle(value)
    },
  }
}

function findUseAsTitleContent(
  document: DocumentT,
  customType: StaticCustomType,
): string | undefined {
  let title: string | undefined = undefined
  traverseDocument({
    document,
    customType,
  })({
    transformWidget: ({ content, model }) => {
      if (title) return undefined
      if (
        (isRichTextContent(content) && model?.type === "StructuredText") ||
        (isTextContent(content) && model?.type === "Text") ||
        (isUIDContent(content) && model?.type === "UID")
      ) {
        if (model.config?.useAsTitle) {
          title = extractTextFromUseAsTitleField(content)
        }
      }
      return undefined
    },
  })

  return title
}

function extractTextFromUseAsTitleField(
  contentField: WidgetContent,
): string | undefined {
  if (!contentField) return undefined

  if (contentField.__TYPE__ === "UIDContent") {
    return contentField.value
  }

  if (contentField.__TYPE__ === "StructuredTextContent") {
    const [firstValue] = contentField.value
    if (
      !firstValue ||
      firstValue.type === "image" ||
      firstValue.type === "embed"
    ) {
      return undefined
    }

    return firstValue.content.text
  }

  if (
    contentField.__TYPE__ === "FieldContent" &&
    contentField.type === "Text"
  ) {
    return contentField.value
  }

  return undefined
}
